import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";

function DocParagraph({ children }) {
  return <p className="text-sm text-muted-foreground leading-relaxed mb-4">{children}</p>;
}

DocParagraph.propTypes = {
  children: PropTypes.node.isRequired,
};

function DocList({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-2 mb-4 text-sm text-muted-foreground leading-relaxed">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

DocList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function LabeledItems({ items }) {
  return (
    <dl className="space-y-4 mb-4">
      {items.map(({ label, text }) => (
        <div key={label}>
          <dt className="font-semibold text-sm mb-1">{label}</dt>
          <dd className="text-sm text-muted-foreground leading-relaxed">{text}</dd>
        </div>
      ))}
    </dl>
  );
}

LabeledItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
};

function DocSubsection({ number, title, paragraphs, listItems, labeledItems, contactLines }) {
  return (
    <div className="mb-6">
      {(number || title) && (
        <h3 className="font-semibold text-base mb-3">
          {number && `${number} `}
          {title}
        </h3>
      )}
      {paragraphs?.map((text) => (
        <DocParagraph key={text}>{text}</DocParagraph>
      ))}
      {labeledItems?.length > 0 && <LabeledItems items={labeledItems} />}
      {listItems?.length > 0 && <DocList items={listItems} />}
      {contactLines?.map(({ label, value, href }) => (
        <p key={label} className="text-sm text-muted-foreground leading-relaxed mb-2">
          {label}{" "}
          {href ? (
            <a href={href} className="text-primary hover:underline">
              {value}
            </a>
          ) : (
            value
          )}
        </p>
      ))}
    </div>
  );
}

DocSubsection.propTypes = {
  number: PropTypes.string,
  title: PropTypes.string,
  paragraphs: PropTypes.arrayOf(PropTypes.string),
  listItems: PropTypes.arrayOf(PropTypes.string),
  labeledItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
  contactLines: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      href: PropTypes.string,
    })
  ),
};

function DocSection({
  number,
  title,
  paragraphs,
  listItems,
  labeledItems,
  subsections,
  contactLines,
  paragraphsAfterList,
  paragraphsAfterContact,
}) {
  return (
    <section className="mb-10">
      <h2 className="font-bold text-lg sm:text-xl mb-4">
        {number}. {title}
      </h2>
      {paragraphs?.map((text) => (
        <DocParagraph key={text}>{text}</DocParagraph>
      ))}
      {labeledItems?.length > 0 && <LabeledItems items={labeledItems} />}
      {listItems?.length > 0 && <DocList items={listItems} />}
      {paragraphsAfterList?.map((text) => (
        <DocParagraph key={text}>{text}</DocParagraph>
      ))}
      {subsections?.map((subsection) => (
        <DocSubsection key={subsection.number || subsection.title} {...subsection} />
      ))}
      {contactLines?.map(({ label, value, href }) => (
        <p key={label} className="text-sm text-muted-foreground leading-relaxed mb-2">
          {label}{" "}
          {href ? (
            <a href={href} className="text-primary hover:underline">
              {value}
            </a>
          ) : (
            value
          )}
        </p>
      ))}
      {paragraphsAfterContact?.map((text) => (
        <DocParagraph key={text}>{text}</DocParagraph>
      ))}
    </section>
  );
}

DocSection.propTypes = {
  number: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  paragraphs: PropTypes.arrayOf(PropTypes.string),
  paragraphsAfterList: PropTypes.arrayOf(PropTypes.string),
  paragraphsAfterContact: PropTypes.arrayOf(PropTypes.string),
  listItems: PropTypes.arrayOf(PropTypes.string),
  labeledItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
  subsections: PropTypes.arrayOf(PropTypes.object),
  contactLines: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      href: PropTypes.string,
    })
  ),
};

const metaShape = PropTypes.shape({
  company: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  lastUpdated: PropTypes.string,
  contactLine: PropTypes.string,
  subtitle: PropTypes.string,
});

export function LegalDocumentPage({ meta, intro, sections, helmetDescription }) {
  return (
    <>
      <Helmet>
        <title>
          {meta.title} | Fortify
        </title>
        <meta name="description" content={helmetDescription} />
      </Helmet>

      <section className="py-16 sm:py-20 bg-secondary/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 text-center">
          <p className="text-xs font-semibold tracking-widest text-muted-foreground uppercase mb-2">
            {meta.company}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{meta.title}</h1>
          {meta.subtitle && (
            <p className="mt-2 text-sm font-medium text-muted-foreground">{meta.subtitle}</p>
          )}
          {meta.lastUpdated && (
            <p className="mt-3 text-sm text-muted-foreground">{meta.lastUpdated}</p>
          )}
          {meta.contactLine && (
            <p className="mt-1 text-sm text-muted-foreground">{meta.contactLine}</p>
          )}
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 max-w-4xl">
          {intro && <DocParagraph>{intro}</DocParagraph>}
          {sections.map((section) => (
            <DocSection key={section.number} {...section} />
          ))}
        </div>
      </section>
    </>
  );
}

LegalDocumentPage.propTypes = {
  meta: metaShape.isRequired,
  intro: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  helmetDescription: PropTypes.string.isRequired,
};
